import { useState, useEffect, useRef, useCallback } from 'react'
import { fmtMonto, hoyISO } from '../utils/fecha'
import { useNavigate } from 'react-router-dom'
import { notasVentaApi } from '../api/notasVenta'
import BuscadorCliente from '../components/BuscadorCliente'
import PasteExcelModal from '../components/PasteExcelModal'
import ModalAltaCliente from '../components/ModalAltaCliente'
import ModalCondicionFiscal from '../components/ModalCondicionFiscal'
import ModalPreciosArticulo from '../components/ModalPreciosArticulo'
import SelectorArticulosModal from '../components/SelectorArticulosModal'
import TraerCarritoModal from '../components/TraerCarritoModal'
import ModalListaClientes from '../components/ModalListaClientes'
import BuscadorArticuloGrande from '../components/BuscadorArticuloGrande'
import ModalListaVendedores from '../components/ModalListaVendedores'
import api from '../api/client'
import { useAuth } from '../context/AuthContext'
import { etiquetaCliente } from '../utils/cliente'
import useDirtyGuard from '../hooks/useDirtyGuard'
import Boton from '../components/Boton'
import { PROVINCIAS, esCapitalFederal, matchProvincia } from '../data/argentina'
import CampoLocalidad from '../components/CampoLocalidad'
import CampoDireccion from '../components/CampoDireccion'
import CotizadorEnvio from '../components/CotizadorEnvio'
import { destinosParaPayload, repartoDe } from '../utils/envioDestinos'
import { colors, text, space, radius, sx } from '../design/tokens'
import { conteoItems, textoConteo } from '../utils/conteoItems'
import CeldaStockPresea, { CabeceraStockPresea } from '../components/CeldaStockPresea'
import { anchoStock, depositosDe } from '../utils/stockPresea'
import { listaParaBuscar } from '../utils/listaVenta'

/**
 * PANTALLA DE PRUEBA de la Nota de Venta (pedido Martin, 2 sep 2026).
 * Convive con la clásica hasta el OK; crea NVs REALES en borrador.
 *
 * Qué cambia respecto de la clásica:
 *  1. Los ARTÍCULOS van arriba de todo (lo más importante, visible sin scroll).
 *  2. Buscador con desplegable grande (≈10 resultados) y manejo por teclado.
 *  3. Carga en GRILLA tipo Excel: flechas para moverse entre celdas, columnas
 *     de ancho FIJO (nada se agranda al tipear).
 *  4. Totales SIEMPRE a la vista (columna sticky a la derecha).
 *  5. Factura A: desglose Neto + IVA + Percepciones + Total.
 *  6. Cliente compacto arriba; observaciones/entrega abajo (secundarias).
 */

const PREFIJO_MEMBRESIA = 'WC000'

const DIR_ENVIO_VACIA = { receptor_nombre: '', telefono: '', calle: '', piso: '', localidad: '', provincia: '', codigo_postal: '' }

const esPrecioPropio = (it) => {
  const cod = (it.articulo?.codigo_alfa || '').toUpperCase()
  return cod === 'ENV0000' || cod.startsWith('GIF')
}

const FORM_VACIO = {
  cliente_id: null,
  cliente: null,
  vendedor_id: '',
  vendedor_codigo: '',
  vendedor_nombre: '',
  lista_precios: '',
  fecha: hoyISO(),
  fecha_entrega_est: '',
  horario_entrega: '',
  notas_entrega: '',
  indicaciones_packaging: '',
  orden_compra_cliente: '',
  dirEnvio_usar_cliente: true,
  dirEnvio_retira: null,
  direccion_envio: DIR_ENVIO_VACIA,
  envio_destinos: [],      // envío múltiple (T80): destinos con sus artículos — docs/diseno-envio-multiple.md
  condicion_venta: '1',
  deposito: '100',
  observaciones: '',
  descuento_gral: '',
  es_mkt: false,
  items: [],
}

// Solo dígitos, coma/punto decimal y (para gift) el signo menos.
const limpiarNum = (v, negativo = false) => {
  let t = String(v).replace(',', '.').replace(/[^\d.-]/g, '')
  if (!negativo) t = t.replace(/-/g, '')
  return t
}

export default function NotaVentaFormV2Page() {
  const navigate = useNavigate()
  const { user, hasAnyRole } = useAuth()
  const esAdmin = hasAnyRole(['administrador', 'super_administrador'])

  const [form, setForm] = useState(FORM_VACIO)
  const [condiciones, setCondiciones] = useState([])
  const [guardando, setGuardando] = useState(false)
  const [errores, setErrores] = useState({})
  const [mensajeOk, setMensajeOk] = useState('')
  const [percep, setPercep] = useState(null)
  const [envioFuera, setEnvioFuera] = useState(false)

  useDirtyGuard(!guardando && !mensajeOk && (form.items.length > 0 || !!form.cliente_id))

  const [articuloBuscandoIdx, setArticuloBuscandoIdx] = useState(null)
  const [pasteText, setPasteText] = useState('')
  const [showPasteModal, setShowPasteModal] = useState(false)
  const [showAltaCliente, setShowAltaCliente] = useState(false)
  const [showCondFiscal, setShowCondFiscal] = useState(false)
  const [showEditarCliente, setShowEditarCliente] = useState(false)
  const [showBuscadorClienteModal, setShowBuscadorClienteModal] = useState(false)
  const [showBuscarArticulos, setShowBuscarArticulos] = useState(false)
  // Productos habituales del cliente (Martin, 21 sep 2026): el MISMO modal de
  // artículos, acotado a lo que ese cliente compra de verdad.
  const [showHabituales, setShowHabituales] = useState(false)
  // «🛒 Traer carrito»: el carrito de la tienda del que salieron renglones (queda tomado al guardar).
  const [showTraerCarrito, setShowTraerCarrito] = useState(false)
  const [carritoTraido, setCarritoTraido] = useState(null)
  const [showVendedoresModal, setShowVendedoresModal] = useState(false)
  const [articuloPrecios, setArticuloPrecios] = useState(null)
  const [sugerenciaWc, setSugerenciaWc] = useState(null)
  const [sugVg, setSugVg] = useState(null)
  const [sugVgCerrada, setSugVgCerrada] = useState(false)
  const [stockPresea, setStockPresea] = useState({ porCodigo: null, depositos: [], cargando: false, error: null })

  // ── Grilla tipo Excel: refs de celdas + navegación con flechas ────────────
  // Columnas editables (en orden visual): 0 cantidad · 1 precio · 2 desc %
  const celdas = useRef({})
  const buscadorRef = useRef(null)
  const registrarCelda = (i, c) => (el) => { celdas.current[`${i}:${c}`] = el }
  const focoCelda = useCallback((i, c) => {
    const el = celdas.current[`${i}:${c}`]
    if (el) { el.focus(); el.select() }
  }, [])
  const NCOLS = 3

  const navegar = (i, c) => (e) => {
    const el = e.target
    const alBordeIzq = el.selectionStart === 0 && el.selectionEnd === 0
    const alBordeDer = el.selectionStart === el.value.length && el.selectionEnd === el.value.length
    const todoSeleccionado = el.selectionStart === 0 && el.selectionEnd === el.value.length && el.value.length > 0

    if (e.key === 'ArrowDown' || e.key === 'Enter') {
      e.preventDefault()
      if (i + 1 < form.items.length) focoCelda(i + 1, c)
      else buscadorRef.current?.focus()               // última fila: a buscar otro artículo
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      if (i > 0) focoCelda(i - 1, c)
      else buscadorRef.current?.focus()
    } else if (e.key === 'ArrowLeft' && (alBordeIzq || todoSeleccionado)) {
      e.preventDefault()
      if (c > 0) focoCelda(i, c - 1)
    } else if (e.key === 'ArrowRight' && (alBordeDer || todoSeleccionado)) {
      e.preventDefault()
      if (c + 1 < NCOLS) focoCelda(i, c + 1)
    } else if (e.key === 'F2') {
      e.preventDefault()
      setArticuloBuscandoIdx(i)
    }
  }

  const bajarAGrilla = () => { if (form.items.length > 0) focoCelda(0, 0) }

  // ── Lista de precios (misma regla que la pantalla clásica) ────────────────
  const userLista = user?.lista_precios || null
  const listaEfectiva = userLista || form.cliente?.lista_precios || null

  const [preciosLista, setPreciosLista] = useState({})
  useEffect(() => {
    if (!listaEfectiva) { setPreciosLista({}); return }
    api.get(`/listas-precios/codigo/${encodeURIComponent(listaEfectiva)}/items`).then(({ data }) => {
      const mapa = {}
      const items = Array.isArray(data) ? data : (data.data || [])
      items.forEach(it => { mapa[it.articulo_id] = parseFloat(it.precio) })
      setPreciosLista(mapa)
      setForm(f => ({ ...f, items: f.items.map(it => {
        if (it.precio_manual || esPrecioPropio(it)) return it
        const p = mapa[it.articulo_id]
        return p != null ? { ...it, precio_lista: p, precio_manual: false } : it
      }) }))
    }).catch(() => setPreciosLista({}))
  }, [listaEfectiva])

  useEffect(() => {
    api.get('/condiciones-venta')
      .then(({ data }) => setCondiciones(Array.isArray(data) ? data : (data.data || [])))
      .catch(() => setCondiciones([]))
  }, [])

  // El vendedor arranca siendo quien carga (igual que la clásica).
  useEffect(() => {
    if (!user?.id) return
    setForm(f => {
      if (f.vendedor_id && f.vendedor_codigo) return f
      return {
        ...f,
        vendedor_id: f.vendedor_id || user.id,
        vendedor_codigo: f.vendedor_codigo || (user.codigo_vendedor ? String(user.codigo_vendedor) : ''),
        vendedor_nombre: f.vendedor_nombre || (user.codigo_vendedor ? (user.nombre || '') : ''),
      }
    })
  }, [user])

  // Libreta de direcciones del cliente.
  const [dirGuardadas, setDirGuardadas] = useState([])
  useEffect(() => {
    if (!form.cliente_id) { setDirGuardadas([]); return }
    api.get(`/clientes/${form.cliente_id}/direcciones`)
      .then(({ data }) => setDirGuardadas(Array.isArray(data) ? data : []))
      .catch(() => setDirGuardadas([]))
  }, [form.cliente_id])

  // Ctrl+V con tabulaciones fuera de un input → pegar desde Excel.
  useEffect(() => {
    const handler = (e) => {
      const tag = document.activeElement?.tagName
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(tag)) return
      const texto = e.clipboardData?.getData('text') || ''
      if (texto.includes('\t')) {
        e.preventDefault()
        setPasteText(texto)
        setShowPasteModal(true)
      }
    }
    document.addEventListener('paste', handler)
    return () => document.removeEventListener('paste', handler)
  }, [])

  // F6 — búsqueda avanzada de cliente.
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'F6') { e.preventDefault(); setShowBuscadorClienteModal(true) }
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [])

  // ── Ítems ─────────────────────────────────────────────────────────────────
  const pedirSugerenciaWineClub = async (articulo) => {
    const sku = String(articulo.codigo_alfa || '').toUpperCase()
    if (!sku.startsWith(PREFIJO_MEMBRESIA)) return
    try {
      const { data } = await api.get('/wine-club/sugerencia-nv', {
        params: { sku, cliente_id: form.cliente_id || undefined },
      })
      if (data?.nivel) setSugerenciaWc(data)
    } catch { /* la sugerencia nunca frena la carga */ }
  }

  const agregarItemConPrecio = (articulo, precio) => {
    const sinPrecio = precio == null
    setForm(f => ({
      ...f,
      items: [...f.items, {
        articulo_id: articulo.id,
        articulo: { id: articulo.id, codigo_alfa: articulo.codigo_alfa, descripcion: articulo.descripcion, codigo_presea: articulo.codigo_presea, factura_como_id: articulo.factura_como_id },
        cantidad: 1,
        precio_lista: sinPrecio ? '' : precio,
        descuento: '',
        observacion: '',
        precio_manual: sinPrecio,
      }],
    }))
    pedirSugerenciaWineClub(articulo)
    // El renglón recién agregado recibe el foco en Cantidad, listo para tipear.
    setTimeout(() => focoCelda(form.items.length, 0), 50)
  }

  const agregarArticulo = (articulo) => {
    const precioEnLista = preciosLista[articulo.id]
    if (!userLista || precioEnLista == null) { setArticuloPrecios(articulo); return }
    agregarItemConPrecio(articulo, precioEnLista)
  }

  const actualizarItem = (index, campo, valor) => {
    setForm(f => ({
      ...f,
      items: f.items.map((it, i) => i === index ? { ...it, [campo]: valor } : it),
    }))
  }

  const eliminarItem = (index) => {
    setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== index) }))
  }

  const vaciarItems = () => {
    if (form.items.length === 0) return
    if (!window.confirm(`¿Eliminar los ${form.items.length} artículos de la nota?`)) return
    setForm(f => ({ ...f, items: [] }))
  }

  const handleArticuloSeleccionado = (articulo) => {
    if (articuloBuscandoIdx === null) return
    const precio = preciosLista[articulo.id]
    const sinPrecio = precio == null
    setForm(f => ({
      ...f,
      items: f.items.map((it, i) => i === articuloBuscandoIdx ? {
        ...it,
        articulo_id: articulo.id,
        articulo: { id: articulo.id, codigo_alfa: articulo.codigo_alfa, descripcion: articulo.descripcion, codigo_presea: articulo.codigo_presea, factura_como_id: articulo.factura_como_id },
        precio_lista: sinPrecio ? it.precio_lista : precio,
        precio_manual: sinPrecio,
      } : it),
    }))
    setArticuloBuscandoIdx(null)
  }

  const handleConfirmPaste = (nuevoItems) => {
    setForm(f => ({ ...f, items: [...f.items, ...nuevoItems] }))
  }

  const handleClienteCreado = (cliente) => {
    setForm(f => ({ ...f, cliente_id: cliente.id, cliente }))
    setShowAltaCliente(false)
  }

  // Recibe [{articulo, cantidad}] del selector múltiple (cantidad elegida en el panel).
  const handleArticulosMulti = (seleccion) => {
    setForm(f => ({
      ...f,
      items: [...f.items, ...seleccion.map(({ articulo: a, cantidad }) => {
        const precio = preciosLista[a.id]
        const sinPrecio = precio == null
        return {
          articulo_id: a.id,
          articulo: { id: a.id, codigo_alfa: a.codigo_alfa, descripcion: a.descripcion, codigo_presea: a.codigo_presea, factura_como_id: a.factura_como_id },
          cantidad: cantidad || 1,
          precio_lista: sinPrecio ? '' : precio,
          descuento: '',
          observacion: '',
          precio_manual: sinPrecio,
        }
      })],
    }))
  }

  // Renglones del carrito de la tienda: con la plata del checkout (precio
  // propio), se SUMAN a los que ya hay. La ficha solo si el vendedor aceptó la sugerida.
  const handleTraerCarrito = ({ carrito, renglones, avisos, clienteSugerido }) => {
    const nota = `Carrito web ${carrito.codigo} traído por ${user?.nombre || 'el vendedor'}.`
      + (avisos.length ? ` ⚠ PRECIO DISTINTO AL DEL CARRITO: ${avisos.join('; ')}.` : '')
    setForm(f => ({
      ...f,
      ...(clienteSugerido && !f.cliente_id ? { cliente_id: clienteSugerido.id, cliente: clienteSugerido } : {}),
      observaciones: [f.observaciones?.trim(), nota].filter(Boolean).join(' '),
      items: [...f.items, ...renglones.map(r => ({
        articulo_id: r.articulo_id,
        articulo: r.articulo,
        cantidad: r.cantidad,
        precio_lista: r.precio_lista,
        descuento: r.descuento || '',
        observacion: r.observacion || '',
        precio_manual: true,
      }))],
    }))
    if (carrito.tipo !== 'compartido') setCarritoTraido(carrito)
    setShowTraerCarrito(false)
  }

  const handlePasteButtonClick = async () => {
    try {
      const texto = await navigator.clipboard.readText()
      if (texto) { setPasteText(texto); setShowPasteModal(true) }
    } catch {
      alert('No se pudo leer el portapapeles. Hacé clic fuera de los campos de texto y presioná Ctrl+V.')
    }
  }

  const sumarVinosDelClub = () => {
    const nuevos = (sugerenciaWc?.items || []).map(it => ({
      articulo_id: it.articulo_id,
      articulo: { id: it.articulo_id, codigo_alfa: it.codigo_alfa, descripcion: it.descripcion },
      cantidad: 1,
      precio_lista: it.precio_lista || '',
      descuento: 100,
      observacion: '',
      precio_manual: false,
    }))
    setForm(f => ({ ...f, items: [...f.items, ...nuevos] }))
    setSugerenciaWc(null)
  }

  const consultarStock = async () => {
    const codigos = [...new Set(form.items.map(it => it.articulo?.codigo_alfa).filter(Boolean))]
    if (codigos.length === 0) return
    setStockPresea(s => ({ ...s, cargando: true, error: null }))
    try {
      const { data } = await notasVentaApi.stockPresea(codigos)
      setStockPresea({ porCodigo: data.stock, depositos: depositosDe(data.stock), cargando: false, error: null })
    } catch (err) {
      setStockPresea(s => ({
        ...s,
        cargando: false,
        error: err.response?.data?.message || 'Presea no respondió la consulta de stock.',
      }))
    }
  }

  // ── Totales ───────────────────────────────────────────────────────────────
  const calcularTotales = () => {
    const subtotalItems = form.items.reduce((acc, it) => {
      const pf = (parseFloat(it.precio_lista) || 0) * (1 - (parseFloat(it.descuento) || 0) / 100)
      return acc + pf * (parseInt(it.cantidad) || 0)
    }, 0)
    const total = subtotalItems * (1 - (parseFloat(form.descuento_gral) || 0) / 100)
    return { subtotalItems: subtotalItems.toFixed(2), total: total.toFixed(2) }
  }
  const { subtotalItems, total } = calcularTotales()

  // Letra de factura del cliente — misma regla que Cliente::letraFactura().
  const letraFactura = (() => {
    const c = form.cliente
    if (!c) return null
    const forzada = (c.tipo_factura || '').toUpperCase()
    return ['A', 'B'].includes(forzada) ? forzada : (parseFloat(c.tipo_resp) === 1 ? 'A' : 'B')
  })()

  // Desglose fiscal EN VIVO (debounced): neto/IVA con la alícuota real de cada
  // artículo + percepciones — lo calcula el backend, la pantalla solo muestra.
  useEffect(() => {
    const lineas = form.items
      .filter(it => it.articulo_id && (parseFloat(it.precio_lista) || 0) !== 0 && (parseInt(it.cantidad) || 0) > 0)
      .map(it => ({
        articulo_id: it.articulo_id,
        cantidad: parseInt(it.cantidad) || 0,
        precio_final: (parseFloat(it.precio_lista) || 0) * (1 - (parseFloat(it.descuento) || 0) / 100),
      }))
    if (!form.cliente_id || lineas.length === 0) { setPercep(null); return }
    const t = setTimeout(() => {
      api.post('/notas-venta/percepciones', {
        cliente_id: form.cliente_id,
        descuento_gral: parseFloat(form.descuento_gral) || 0,
        lineas,
      }).then(({ data }) => setPercep(data)).catch(() => setPercep(null))
    }, 600)
    return () => clearTimeout(t)
  }, [form.cliente_id, form.descuento_gral, form.items])

  // Sugerencia sacacorchos VG (idéntica a la clásica).
  useEffect(() => {
    const ids = form.items.map(it => it.articulo_id).filter(Boolean)
    if (!form.cliente_id || ids.length === 0) { setSugVg(null); return }
    const t = setTimeout(() => {
      api.post('/notas-venta/sugerencia-vg', { cliente_id: form.cliente_id, articulo_ids: ids })
        .then(({ data }) => setSugVg(data.sugerir ? data.articulo : null))
        .catch(() => setSugVg(null))
    }, 600)
    return () => clearTimeout(t)
  }, [form.cliente_id, form.items])
  const sugVgVisible = sugVg && !sugVgCerrada
    && !form.items.some(it => it.articulo?.codigo_alfa === sugVg.codigo_alfa)

  // Aviso zona Interior (Andreani), idéntico a la clásica.
  useEffect(() => {
    const src = form.dirEnvio_usar_cliente
      ? { localidad: form.cliente?.localidad, cp: form.cliente?.codigo_postal }
      : { localidad: form.direccion_envio?.localidad, cp: form.direccion_envio?.codigo_postal }
    if (!src.localidad && !src.cp) { setEnvioFuera(false); return }
    const t = setTimeout(() => {
      api.get('/zonas-resolver', { params: { localidad: src.localidad || '', cp: src.cp || '' } })
        .then(({ data }) => setEnvioFuera(!!data.fuera_reparto_propio))
        .catch(() => setEnvioFuera(false))
    }, 600)
    return () => clearTimeout(t)
  }, [form.dirEnvio_usar_cliente, form.cliente, form.direccion_envio?.localidad, form.direccion_envio?.codigo_postal])

  // Líneas para el cotizador de envío (precio con descuento, IVA incluido como en la NV).
  const lineasEnvio = form.items
    .filter(it => it.articulo_id && (parseInt(it.cantidad) || 0) > 0)
    .map(it => ({
      articulo_id: it.articulo_id, sku: it.articulo?.codigo_alfa, descripcion: it.articulo?.descripcion,
      cantidad: parseInt(it.cantidad) || 0,
      precio: (parseFloat(it.precio_lista) || 0) * (1 - (parseFloat(it.descuento) || 0) / 100),
    }))
  const direccionCotizar = form.dirEnvio_usar_cliente
    ? { provincia: form.cliente?.provincia, localidad: form.cliente?.localidad, codigo_postal: form.cliente?.codigo_postal }
    : form.direccion_envio
  // El cotizador devuelve el costo; acá se inserta (o reemplaza) la línea ENV0000 con precio propio.
  const agregarLineaEnvio = ({ costo, detalle, articulo_envio_id }) => setForm(f => ({
    ...f,
    items: [...f.items.filter(it => it.articulo?.codigo_alfa !== 'ENV0000'), {
      articulo_id: articulo_envio_id,
      articulo: { id: articulo_envio_id, codigo_alfa: 'ENV0000', descripcion: detalle },
      // La descripción viaja a la NV (max 120): el papel dice qué envío es, no "ENVIO" a secas.
      cantidad: 1, precio_lista: Math.round(costo * 100) / 100, descuento: '', observacion: '', descripcion: detalle.slice(0, 120), precio_manual: true,
    }],
  }))

  // ── Guardar ───────────────────────────────────────────────────────────────
  const buildPayload = () => {
    let direccionEnvio = null
    if (form.dirEnvio_retira) {
      direccionEnvio = form.dirEnvio_retira
    } else if (form.dirEnvio_usar_cliente) {
      const receptor = form.direccion_envio?.receptor_nombre?.trim() || null
      const tel = form.direccion_envio?.telefono?.trim() || null
      if (receptor || tel) {
        direccionEnvio = { receptor_nombre: receptor, telefono: tel, calle: null, piso: null, localidad: null, provincia: null, codigo_postal: null }
      }
      if (form.direccion_envio?.retira === false) {
        direccionEnvio = { ...(direccionEnvio || {}), retira: false }
      }
    }
    if (!form.dirEnvio_retira && !form.dirEnvio_usar_cliente) {
      const d = form.direccion_envio || {}
      const tieneAlgo = Object.values(d).some(v => v && String(v).trim() !== '')
      if (tieneAlgo) {
        direccionEnvio = {
          receptor_nombre: d.receptor_nombre?.trim() || null,
          telefono:        d.telefono?.trim() || null,
          calle:           d.calle?.trim() || null,
          piso:            d.piso?.trim() || null,
          localidad:       d.localidad?.trim() || null,
          provincia:       d.provincia?.trim() || null,
          codigo_postal:   d.codigo_postal?.trim() || null,
        }
      }
    }
    return {
      cliente_id: form.cliente_id,
      vendedor_id: form.vendedor_id ? parseInt(form.vendedor_id) : null,
      vendedor_codigo: form.vendedor_codigo ? parseInt(form.vendedor_codigo) : null,
      // Un carrito de la tienda es canal web: LISTA1, salvo que el usuario tenga lista fija.
      lista_precios: userLista || (carritoTraido ? 'LISTA1' : listaEfectiva) || null,
      carrito_web_id: carritoTraido?.id || null,
      fecha_entrega_est: form.fecha_entrega_est || null,
      horario_entrega: form.fecha_entrega_est ? (form.horario_entrega?.trim() || null) : null,
      notas_entrega: form.notas_entrega?.trim() || null,
      indicaciones_packaging: form.indicaciones_packaging?.trim() || null,
      orden_compra_cliente: form.orden_compra_cliente?.trim() || null,
      direccion_envio: direccionEnvio,
      envio_destinos: destinosParaPayload(form.envio_destinos, lineasEnvio),
      condicion_venta: form.condicion_venta ? parseInt(form.condicion_venta) : null,
      deposito: form.deposito ? parseInt(form.deposito) : null,
      observaciones: form.observaciones,
      descuento_gral: parseFloat(form.descuento_gral) || 0,
      es_mkt: !!form.es_mkt,
      items: form.items.map(it => ({
        articulo_id: it.articulo_id,
        cantidad: parseInt(it.cantidad),
        precio_lista: parseFloat(it.precio_lista),
        descuento: parseFloat(it.descuento) || 0,
        observacion: it.observacion || null,
        descripcion: it.descripcion?.trim() || null,
      })),
    }
  }

  const ejecutarGuardar = async () => {
    setGuardando(true)
    // Envío múltiple: un reparto que no cierra no se guarda (la planilla del T80 no podría cumplirlo).
    if (form.envio_destinos.length > 0 && !repartoDe(form.envio_destinos, lineasEnvio).exacto) {
      setErrores({ envio_destinos: 'El reparto entre destinos no cierra: cada artículo tiene que quedar asignado exacto.' })
      alert('El reparto del envío múltiple no cierra: cada artículo tiene que quedar asignado exacto entre los destinos.')
      return
    }
    setErrores({})
    try {
      const { data: nv } = await notasVentaApi.crear(buildPayload())
      setMensajeOk(`NV ${nv.numero_lgr} guardada en borrador.`)
      setTimeout(() => navigate(`/notas-venta/${nv.id}`), 1200)
    } catch (err) {
      const data = err.response?.data
      setErrores(data?.errors || { general: [data?.message || 'Error al guardar.'] })
    } finally { setGuardando(false) }
  }

  // ─── Render ───────────────────────────────────────────────────────────────
  const c = form.cliente
  const RESP = { 1: 'Responsable Inscripto', 3.1: 'Monotributo', 4: 'Exento', 5: 'Consumidor Final' }
  const resp = c && c.tipo_resp != null && c.tipo_resp !== '' ? (RESP[parseFloat(c.tipo_resp)] || `Cond. ${c.tipo_resp}`) : null
  const desglose = letraFactura === 'A' && percep && percep.neto > 0 ? percep : null
  const totalFinal = (parseFloat(total) || 0) + (percep?.total || 0)

  return (
    <div style={st.wrap}>
      <div style={st.headerRow}>
        <h2 style={st.title}>Nueva Nota de Venta</h2>
        <Boton variante="ghost" onClick={() => navigate(-1)}>← Volver</Boton>
      </div>

      {mensajeOk && <div style={st.success}>{mensajeOk}</div>}
      {errores.general && <div style={st.errorBox}>{errores.general[0]}</div>}

      <div style={st.grid}>
        {/* ── COLUMNA PRINCIPAL: Cliente → Datos generales → Artículos → Entrega ── */}
        <div style={{ minWidth: 0 }}>

          {/* CLIENTE (boceto aprobado: buscador + ficha condensada en 2 líneas) */}
          <div style={st.card}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: space.md }}>
              <BuscadorCliente
                key={form.cliente_id || 'no-client'}
                value={form.cliente}
                onChange={(cl) => setForm(f => ({ ...f, cliente_id: cl.id, cliente: cl }))}
              />
              <Boton variante="secondary" sm type="button" onClick={() => setShowBuscadorClienteModal(true)} title="Búsqueda avanzada de cliente (F6): nombre, CUIT, DNI, localidad, email">🔍</Boton>
              <Boton variante="primary" sm type="button" onClick={() => setShowAltaCliente(true)} title="Nuevo cliente">+</Boton>
            </div>
            {c ? (
              <div style={st.cliInfo}>
                <div>
                  <b style={{ color: colors.textStrong, fontSize: text.body }}>{etiquetaCliente(c)}</b>
                  {c.cuit && <> · <b>CUIT</b> <span style={sx.mono}>{c.cuit}</span></>}
                  {resp && <> · {resp}</>}
                  {letraFactura && <> · <b>Factura {letraFactura}</b></>}
                  {(userLista || c.lista_precios) && <> · Lista <b>{userLista || c.lista_precios}</b>{userLista ? ' (tu usuario)' : ''}</>}
                  {' '}<button type="button" style={st.linkBtn} onClick={() => setShowEditarCliente(true)} title="Editar la ficha del cliente sin salir de la NV">✏️ editar ficha</button>
                  {' '}<button type="button" style={st.linkBtn} onClick={() => setShowCondFiscal(true)} title="Cambiar CUIT / condición IVA / letra de factura">✏️ fiscal</button>
                </div>
                <div>
                  {[c.domicilio, c.localidad, c.provincia, c.codigo_postal && `CP ${c.codigo_postal}`].filter(Boolean).length > 0 &&
                    <>📍 {[c.domicilio, c.localidad, c.provincia, c.codigo_postal && `CP ${c.codigo_postal}`].filter(Boolean).join(', ')}</>}
                  {c.email && <> &nbsp;·&nbsp; ✉ {c.email}</>}
                  {c.telefono && <> &nbsp;·&nbsp; 📞 {c.telefono}</>}
                  {!userLista && !c.lista_precios && (
                    <span style={{ color: colors.t80Text, fontWeight: 600 }}> &nbsp;·&nbsp; 💲 Sin lista fija — al agregar un artículo elegís el precio</span>
                  )}
                </div>
              </div>
            ) : (
              <span style={st.f6Hint}>F6 abre la búsqueda avanzada (por nombre, CUIT, DNI, localidad o email).</span>
            )}
          </div>

          {/* DATOS GENERALES (boceto: un renglón + observaciones) */}
          <div style={st.card}>
            <h3 style={sx.cardTitle}>Datos generales</h3>
            <div style={st.gDG}>
              <div>
                <label style={st.label}>Vendedor</label>
                <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                  <input
                    type="text" inputMode="numeric"
                    style={{ ...st.input, ...sx.mono, width: 44, flexShrink: 0, textAlign: 'center' }}
                    placeholder="cód"
                    value={form.vendedor_codigo}
                    onChange={e => setForm(f => ({ ...f, vendedor_codigo: limpiarNum(e.target.value).replace(/[.,].*$/, ''), vendedor_nombre: '' }))}
                    onBlur={async () => {
                      const cod = parseInt(form.vendedor_codigo)
                      if (!cod) return
                      try {
                        const { data } = await api.get(`/vendedores/${cod}`)
                        setForm(f => ({ ...f, vendedor_nombre: data.nombre }))
                      } catch {
                        setForm(f => ({ ...f, vendedor_nombre: '(no existe)' }))
                      }
                    }}
                  />
                  <button type="button" style={st.lupaBtn} title="Buscar vendedor" onClick={() => setShowVendedoresModal(true)}>🔍</button>
                  <span style={{ fontSize: text.body, color: colors.text, fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {form.vendedor_nombre || ''}
                  </span>
                </div>
              </div>
              <div>
                <label style={st.label}>Condición de venta</label>
                <select style={st.input} value={form.condicion_venta} onChange={e => setForm(f => ({ ...f, condicion_venta: e.target.value }))}>
                  <option value="">Elegí una condición…</option>
                  {condiciones.map(cv => <option key={cv.codigo} value={cv.codigo}>{cv.descripcion}</option>)}
                  {form.condicion_venta && !condiciones.some(cv => String(cv.codigo) === String(form.condicion_venta)) && (
                    <option value={form.condicion_venta}>Cód. {form.condicion_venta}</option>
                  )}
                </select>
              </div>
              <div>
                <label style={st.label}>Depósito</label>
                <input style={{ ...st.input, ...sx.mono, width: 48, textAlign: 'center' }} type="text" inputMode="numeric" maxLength={3} value={form.deposito}
                  onChange={e => setForm(f => ({ ...f, deposito: limpiarNum(e.target.value).replace(/[.,].*$/, '') }))} placeholder="100" />
              </div>
              <div>
                <label style={st.label}>Orden de compra</label>
                <input style={st.input} value={form.orden_compra_cliente} onChange={e => setForm(f => ({ ...f, orden_compra_cliente: e.target.value }))} maxLength={60} title="Figura en la factura" />
              </div>
              {esAdmin && (
                <div>
                  <label style={st.label}>&nbsp;</label>
                  <label style={{ ...st.mktBox, ...(form.es_mkt ? st.mktBoxOn : {}) }} title="Sin entrega (virtual), sin recibo, vendedor 1. El paquete a Presea sale con 'Sin entrega' preseleccionada.">
                    <input
                      type="checkbox"
                      checked={!!form.es_mkt}
                      onChange={async e => {
                        const on = e.target.checked
                        setForm(f => ({ ...f, es_mkt: on, ...(on ? { vendedor_codigo: '1', vendedor_nombre: '' } : {}) }))
                        if (on) {
                          try { const { data } = await api.get('/vendedores/1'); setForm(f => ({ ...f, vendedor_nombre: data.nombre })) } catch { /* sin red: queda el código */ }
                        }
                      }}
                    />
                    <span style={{ whiteSpace: 'nowrap' }}><b>🎯 Canje MKT</b></span>
                  </label>
                </div>
              )}
            </div>
            <div>
              <label style={st.label}>Observaciones</label>
              <input style={st.input} value={form.observaciones} onChange={e => setForm(f => ({ ...f, observaciones: e.target.value }))} placeholder="Ej: TRANSFERENCIA BANCARIA" />
            </div>
          </div>

          {/* ARTÍCULOS */}
          <div style={st.card}>
            <div style={st.articulosHeader}>
              <h3 style={sx.cardTitle}>Artículos</h3>
              <div style={{ display: 'flex', gap: space.sm, flexWrap: 'wrap' }}>
                {form.items.length > 0 && (
                  <Boton variante="secondary" sm onClick={consultarStock} disabled={stockPresea.cargando}
                    title="Stock EN VIVO en Presea, por depósito">
                    {stockPresea.cargando ? '⏳ Consultando…' : '📦 Consultar stock'}
                  </Boton>
                )}
                {form.items.length > 0 && (
                  // La respuesta de depósito vuelve a la NV: se pide desde la NV ya guardada.
                  <Boton variante="secondary" sm disabled title="Guardá la NV para pedir stock a depósito (la respuesta vuelve a la NV)">
                    🏬 Pedir stock a depósito
                  </Boton>
                )}
                <Boton variante="secondary" sm onClick={() => setShowTraerCarrito(true)} title="Traer el carrito de la tienda por el código que ve el cliente (o su email)">🛒 Traer carrito</Boton>
                <Boton variante="secondary" sm onClick={() => setShowBuscarArticulos(true)} title="Buscar y agregar varios artículos">Buscar artículos</Boton>
                <Boton variante="secondary" sm onClick={handlePasteButtonClick} title="Pegar filas copiadas desde Excel (Ctrl+V)">📋 Pegar desde Excel</Boton>
                <Boton variante="secondary" sm onClick={() => setShowHabituales(true)} disabled={!form.cliente_id}
                  title={form.cliente_id
                    ? 'Lo que este cliente compra: su historia de los últimos 24 meses, lo más comprado primero'
                    : 'Elegí primero el cliente'}>
                  ⭐ Productos habituales
                </Boton>
                {form.items.length > 0 && (
                  <Boton variante="destructive" sm onClick={vaciarItems} title="Eliminar todos los artículos">🗑 Vaciar</Boton>
                )}
              </div>
            </div>

            <BuscadorArticuloGrande
              onSelect={agregarArticulo}
              precios={preciosLista}
              clienteId={form.cliente_id}
              lista={listaParaBuscar(listaEfectiva)}
              inputRef={buscadorRef}
              onBajarAGrilla={bajarAGrilla}
            />

            {sugVgVisible && (
              <div style={st.sugerencia}>
                <span style={{ flex: 1 }}>
                  🍷 <b>Primera compra de vinos guardados de este cliente.</b> Sugerile el
                  sacacorchos de láminas. <span style={{ whiteSpace: 'nowrap' }}>{sugVg.descripcion}{sugVg.precio != null ? ` · $ ${fmtMonto(sugVg.precio)}` : ''}</span>
                </span>
                <Boton variante="secondary" sm onClick={() => agregarItemConPrecio(sugVg, sugVg.precio)}>➕ Agregar</Boton>
                <button onClick={() => setSugVgCerrada(true)} title="No sugerir en esta venta" style={st.cerrarSug}>×</button>
              </div>
            )}

            {sugerenciaWc && (
              <div style={st.sugerencia}>
                <span style={{ flex: 1 }}>
                  <b>Wine Club {sugerenciaWc.nombre}.</b>{' '}
                  {sugerenciaWc.items.length > 0 ? (
                    <>La selección de <b>{sugerenciaWc.etiqueta}</b> lleva {sugerenciaWc.items.length}{' '}
                    {sugerenciaWc.items.length === 1 ? 'vino' : 'vinos'}: {sugerenciaWc.items.map(i => i.descripcion).join(' · ')}.
                    {!sugerenciaWc.del_mes && <em style={{ color: colors.warn, fontStyle: 'normal' }}> Ojo: es la selección de un mes anterior.</em>}</>
                  ) : sugerenciaWc.motivo}
                </span>
                {sugerenciaWc.items.length > 0 && (
                  <Boton variante="primary" sm onClick={sumarVinosDelClub}>Agregar los vinos (100% desc.)</Boton>
                )}
                <Boton variante="ghost" sm onClick={() => setSugerenciaWc(null)}>No, gracias</Boton>
              </div>
            )}

            {stockPresea.error && <div style={st.stockError}>{stockPresea.error}</div>}

            {form.items.length === 0 ? (
              <p style={st.emptyItems}>Buscá artículos arriba, usá "Pegar desde Excel" o presioná Ctrl+V.<br />
                <span style={{ fontSize: text.sec, color: colors.textMeta }}>En la grilla te movés con las flechas ↑↓←→ como en Excel. F2 cambia el artículo del renglón.</span></p>
            ) : (
              <div style={{ overflowX: 'auto', marginTop: space.md }}>
                <table style={{ ...st.tabla, minWidth: 700 + (stockPresea.porCodigo ? anchoStock(stockPresea.depositos.length) : 0) }}>
                  <colgroup>
                    <col style={{ width: 80 }} />
                    <col />
                    <col style={{ width: 58 }} />
                    {stockPresea.porCodigo && <col style={{ width: anchoStock(stockPresea.depositos.length) }} />}
                    <col style={{ width: 96 }} />
                    <col style={{ width: 56 }} />
                    <col style={{ width: 100 }} />
                    <col style={{ width: 106 }} />
                    <col style={{ width: 44 }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th style={st.th}>Código</th>
                      <th style={st.th}>Descripción</th>
                      <th style={{ ...st.th, textAlign: 'right' }}>Cant.</th>
                      {stockPresea.porCodigo && (
                        <th style={st.th}><CabeceraStockPresea depositos={stockPresea.depositos} /></th>
                      )}
                      <th style={{ ...st.th, textAlign: 'right' }}>Precio</th>
                      <th style={{ ...st.th, textAlign: 'right' }}>Desc %</th>
                      <th style={{ ...st.th, textAlign: 'right' }}>P. final</th>
                      <th style={{ ...st.th, textAlign: 'right' }}>Subtotal</th>
                      <th style={st.th}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.items.map((it, i) => {
                      const pf = (parseFloat(it.precio_lista) || 0) * (1 - (parseFloat(it.descuento) || 0) / 100)
                      const subtotal = pf * (parseInt(it.cantidad) || 0)
                      const tieneDescuento = pf < (parseFloat(it.precio_lista) || 0)
                      const sinPrecio = it.precio_manual === true
                        && (it.precio_lista === '' || it.precio_lista == null || Number.isNaN(it.precio_lista))
                      const precioPropio = it.precio_manual === true && !sinPrecio
                      const esGift = (it.articulo?.codigo_alfa || '').toUpperCase().startsWith('GIF')
                      const rowBg = sinPrecio ? colors.attnBg : (tieneDescuento ? colors.warnSoft : undefined)
                      return (
                        <tr key={i} style={{ height: 44, borderBottom: `1px solid ${colors.borderSoft}`, ...(rowBg ? { background: rowBg } : {}) }}>
                          <td style={st.td}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                              <code style={{ ...sx.mono, fontSize: text.sec }}>{it.articulo?.codigo_alfa}</code>
                              <button style={st.lupaBtn} onClick={() => setArticuloBuscandoIdx(i)} title="Cambiar artículo (F2)">🔍</button>
                            </div>
                          </td>
                          <td style={{ ...st.td, overflow: 'hidden' }}>
                            {it.editandoDetalle ? (
                              <input
                                className="celda-grilla"
                                style={{ ...st.celdaInput, fontFamily: 'inherit', textAlign: 'left', width: '100%' }}
                                autoFocus
                                maxLength={120}
                                value={it.descripcion || ''}
                                placeholder={it.articulo?.descripcion}
                                onChange={e => actualizarItem(i, 'descripcion', e.target.value)}
                                onBlur={() => actualizarItem(i, 'editandoDetalle', false)}
                                onKeyDown={e => { if (e.key === 'Enter' || e.key === 'Escape') { e.preventDefault(); actualizarItem(i, 'editandoDetalle', false) } }}
                              />
                            ) : (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                                <span style={st.descTexto} title={it.descripcion?.trim() ? `Detalle propio — el artículo real es: ${it.articulo?.descripcion}` : it.articulo?.descripcion}>
                                  {it.descripcion?.trim() || it.articulo?.descripcion}
                                </span>
                                {it.descripcion?.trim() && <span style={st.badgeDetallePropio}>detalle propio</span>}
                                <button style={st.lupaBtn} title="Cambiar el detalle que figura en factura (no cambia el artículo)" onClick={() => actualizarItem(i, 'editandoDetalle', true)}>✏️</button>
                              </div>
                            )}
                            {sinPrecio && <div style={st.warnSinPrecio}>⚠ Sin precio en lista — ingresá manual.</div>}
                            {precioPropio && <div style={st.notaPrecioPropio} title="Precio fijado al cargarse (promo web, envío cotizado, gift card o precio negociado). No se recotiza.">Precio propio — no se recotiza.</div>}
                          </td>
                          <td style={st.td}>
                            <input
                              ref={registrarCelda(i, 0)}
                              className="celda-grilla"
                              style={st.celdaInput}
                              type="text" inputMode="numeric"
                              value={it.cantidad}
                              onChange={e => actualizarItem(i, 'cantidad', limpiarNum(e.target.value, true).replace(/[.,].*$/, ''))}
                              onKeyDown={navegar(i, 0)}
                              onFocus={e => e.target.select()}
                            />
                          </td>
                          {stockPresea.porCodigo && (
                            <CeldaStockPresea porCodigo={stockPresea.porCodigo} depositos={stockPresea.depositos}
                              codigo={it.articulo?.codigo_alfa} cantidad={it.cantidad} style={st.td} />
                          )}
                          <td style={st.td}>
                            <input
                              ref={registrarCelda(i, 1)}
                              className="celda-grilla"
                              style={st.celdaInput}
                              type="text" inputMode="decimal"
                              value={it.precio_lista}
                              onChange={e => actualizarItem(i, 'precio_lista', limpiarNum(e.target.value, esGift))}
                              onKeyDown={navegar(i, 1)}
                              onFocus={e => e.target.select()}
                              title={esGift ? 'Gift card: admite precio negativo (descuento/ajuste)' : undefined}
                            />
                          </td>
                          <td style={st.td}>
                            <input
                              ref={registrarCelda(i, 2)}
                              className="celda-grilla"
                              style={{ ...st.celdaInput, ...(tieneDescuento ? { borderColor: colors.warnStrong } : {}) }}
                              type="text" inputMode="decimal"
                              value={it.descuento}
                              onChange={e => actualizarItem(i, 'descuento', limpiarNum(e.target.value))}
                              onKeyDown={navegar(i, 2)}
                              onFocus={e => e.target.select()}
                            />
                          </td>
                          <td style={{ ...st.td, ...sx.mono, textAlign: 'right', fontWeight: 600, color: tieneDescuento ? colors.warn : colors.ok }}>
                            ${fmtMonto(pf)}
                          </td>
                          <td style={{ ...st.td, ...sx.mono, textAlign: 'right' }}>${fmtMonto(subtotal)}</td>
                          <td style={{ ...st.td, textAlign: 'center' }}>
                            <Boton variante="destructive" sm onClick={() => eliminarItem(i)} title="Quitar renglón">✕</Boton>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* ── ENTREGA: abajo de los productos (boceto aprobado) ── */}
          <div style={st.card}>
              <h3 style={sx.cardTitle}>Entrega</h3>
              <div style={st.gENT}>
                <div>
                  <label style={st.label}>Fecha estimada</label>
                  <input style={st.input} type="date" value={form.fecha_entrega_est} onChange={e => setForm(f => ({ ...f, fecha_entrega_est: e.target.value }))} />
                </div>
                <div>
                  <label style={st.label}>Horario</label>
                  <input style={st.input} value={form.horario_entrega} onChange={e => setForm(f => ({ ...f, horario_entrega: e.target.value }))} placeholder="Ej: 12 a 18" maxLength={120} title="Se imprime en el remito tal como lo escribas" />
                </div>
                <div>
                  <label style={st.label}>Detalles de entrega</label>
                  <input style={st.input} value={form.notas_entrega} onChange={e => setForm(f => ({ ...f, notas_entrega: e.target.value }))} placeholder="Ej: timbre fondo · llamar al llegar" title="Baja al remito tal como se escriba" />
                </div>
              </div>
              {/* Indicaciones de packaging para el depósito: salen abajo del
                  remito, arriba de la firma (Martin, 15 sep 2026). */}
              <input
                style={{ ...st.input, width: '100%', marginTop: space.sm }}
                value={form.indicaciones_packaging}
                onChange={e => setForm(f => ({ ...f, indicaciones_packaging: e.target.value }))}
                placeholder="Indicaciones de packaging"
                maxLength={1000}
                title="Se imprime abajo del remito (ENTREGAR CON:), en original y duplicado"
              />

              <div style={st.envioBox}>
                {form.dirEnvio_retira ? (
                  <div>
                    <div style={{ fontWeight: 600, color: colors.text, marginBottom: 4 }}>
                      🏬 Retira en sucursal{form.dirEnvio_retira.sucursal ? `: ${form.dirEnvio_retira.sucursal}` : ''} — no se despacha a domicilio
                    </div>
                    <div style={{ fontSize: text.sec, color: colors.textSec, marginBottom: 8 }}>
                      Lo eligió el cliente en el pedido. Guardar la NV no lo cambia.
                    </div>
                    <Boton variante="ghost" sm onClick={() => setForm(f => ({ ...f, dirEnvio_retira: null, dirEnvio_usar_cliente: true, direccion_envio: { ...DIR_ENVIO_VACIA, retira: false } }))}>
                      Cambiar a envío a domicilio
                    </Boton>
                  </div>
                ) : (
                  <label style={{ ...st.label, display: 'flex', alignItems: 'center', gap: 8, marginBottom: space.sm }}>
                    <input
                      type="checkbox"
                      checked={form.dirEnvio_usar_cliente}
                      onChange={e => setForm(f => ({ ...f, dirEnvio_usar_cliente: e.target.checked }))}
                    />
                    Entregar en la dirección del cliente
                  </label>
                )}
                {form.dirEnvio_usar_cliente && !form.dirEnvio_retira && (
                  <div style={st.row2}>
                    <div>
                      <label style={st.label}>Receptor (si no lo recibe el cliente)</label>
                      <input style={st.input} value={form.direccion_envio.receptor_nombre} onChange={e => setForm(f => ({ ...f, direccion_envio: { ...f.direccion_envio, receptor_nombre: e.target.value } }))} placeholder="Opcional" />
                    </div>
                    <div>
                      <label style={st.label}>Teléfono del receptor</label>
                      <input style={st.input} value={form.direccion_envio.telefono} onChange={e => setForm(f => ({ ...f, direccion_envio: { ...f.direccion_envio, telefono: e.target.value } }))} placeholder="Opcional" />
                    </div>
                  </div>
                )}
                {dirGuardadas.length > 0 && !form.dirEnvio_retira && (
                  <select
                    style={{ ...st.input, color: colors.textSub, marginTop: space.sm }}
                    value=""
                    onChange={e => {
                      const d = dirGuardadas.find(x => String(x.id) === e.target.value)?.direccion
                      if (!d) return
                      setForm(f => ({ ...f, dirEnvio_usar_cliente: false, direccion_envio: { ...DIR_ENVIO_VACIA, ...d } }))
                    }}
                  >
                    <option value="">📍 Reusar una dirección ya usada con este cliente…</option>
                    {dirGuardadas.map(d => {
                      const dir = d.direccion || {}
                      const linea = [[dir.calle, dir.piso].filter(Boolean).join(' '), dir.localidad].filter(Boolean).join(', ')
                      return <option key={d.id} value={d.id}>{linea}{dir.receptor_nombre ? ` — recibe ${dir.receptor_nombre}` : ''}{d.veces > 1 ? ` (usada ${d.veces} veces)` : ''}</option>
                    })}
                  </select>
                )}
                {!form.dirEnvio_usar_cliente && !form.dirEnvio_retira && (
                  <>
                    <div style={{ fontSize: text.sec, color: colors.textSec, margin: `${space.sm}px 0` }}>
                      Esta dirección reemplaza la del cliente solo para el envío (no toca los datos del cliente).
                    </div>
                    <div style={st.row2}>
                      <div>
                        <label style={st.label}>Receptor (nombre)</label>
                        <input style={st.input} value={form.direccion_envio.receptor_nombre} onChange={e => setForm(f => ({ ...f, direccion_envio: { ...f.direccion_envio, receptor_nombre: e.target.value } }))} />
                      </div>
                      <div>
                        <label style={st.label}>Teléfono</label>
                        <input style={st.input} value={form.direccion_envio.telefono} onChange={e => setForm(f => ({ ...f, direccion_envio: { ...f.direccion_envio, telefono: e.target.value } }))} placeholder="+54 11 1234-5678" />
                      </div>
                    </div>
                    <div style={{ marginTop: space.sm }}>
                      <label style={st.label}>Calle y número</label>
                      <CampoDireccion
                        value={form.direccion_envio.calle}
                        onChange={v => setForm(f => ({ ...f, direccion_envio: { ...f.direccion_envio, calle: v } }))}
                        onPlaceSelected={d => setForm(f => ({
                          ...f,
                          direccion_envio: {
                            ...f.direccion_envio,
                            calle: d.calle || f.direccion_envio.calle,
                            piso: f.direccion_envio.piso || d.detalle_extra || '',
                            localidad: d.localidad || f.direccion_envio.localidad,
                            provincia: d.provincia || f.direccion_envio.provincia,
                            codigo_postal: d.codigo_postal || f.direccion_envio.codigo_postal,
                          },
                        }))}
                        inputStyle={st.input}
                        placeholder="Calle y número"
                      />
                    </div>
                    <div>
                      <label style={st.label}>Piso / Depto</label>
                      <input style={st.input} value={form.direccion_envio.piso} onChange={e => setForm(f => ({ ...f, direccion_envio: { ...f.direccion_envio, piso: e.target.value } }))} placeholder="Ej: 6° B — o timbre, casa del fondo…" />
                    </div>
                    {(() => {
                      const env = form.direccion_envio
                      const setEnv = (k, v) => setForm(f => ({ ...f, direccion_envio: { ...f.direccion_envio, [k]: v } }))
                      const provinciaCanon = matchProvincia(env.provincia)
                      const esCabaEnv = esCapitalFederal(provinciaCanon)
                      return (
                        <div style={{ ...st.row2, marginTop: space.sm }}>
                          <div>
                            <label style={st.label}>Provincia</label>
                            <select style={st.input} value={provinciaCanon} onChange={e => {
                              const nueva = e.target.value
                              setForm(f => ({ ...f, direccion_envio: { ...f.direccion_envio, provincia: nueva, localidad: esCapitalFederal(nueva) ? '' : f.direccion_envio.localidad } }))
                            }}>
                              <option value="">— Provincia —</option>
                              {PROVINCIAS.map(p => <option key={p} value={p}>{p}</option>)}
                            </select>
                          </div>
                          {!esCabaEnv && (
                            <div>
                              <label style={st.label}>Localidad</label>
                              <CampoLocalidad provincia={provinciaCanon} value={env.localidad} onChange={v => setEnv('localidad', v)} inputStyle={st.input} cp={env.codigo_postal} />
                            </div>
                          )}
                          <div>
                            <label style={st.label}>Código postal{esCabaEnv ? ' *' : ''}</label>
                            <input style={st.input} value={env.codigo_postal} onChange={e => setEnv('codigo_postal', e.target.value)} required={esCabaEnv} />
                          </div>
                        </div>
                      )
                    })()}
                  </>
                )}
                {envioFuera && (
                  <div style={st.avisoInterior}>
                    ⚠ Entrega a más de ~50 km (zona Interior): no corresponde transporte propio — el envío va por <b>Andreani</b>.
                  </div>
                )}
                <CotizadorEnvio
                  lineas={lineasEnvio}
                  direccion={direccionCotizar}
                  destinos={form.envio_destinos}
                  onDestinosChange={d => setForm(f => ({ ...f, envio_destinos: d }))}
                  onAgregarLinea={agregarLineaEnvio}
                  inputStyle={st.input}
                />
              </div>
          </div>
        </div>

        {/* ── TOTALES: sticky, siempre a la vista ── */}
        <div>
          <div style={st.totalesCard}>
            <h3 style={sx.cardTitle}>Totales</h3>
            <div style={st.totalRow}>
              <span>Artículos</span>
              <strong>{textoConteo(conteoItems(form.items))}</strong>
            </div>
            <div style={st.totalRow}>
              <span>Subtotal ítems</span>
              <strong style={sx.mono}>${fmtMonto(subtotalItems)}</strong>
            </div>
            <div style={st.totalRow}>
              <label style={{ ...st.label, whiteSpace: 'nowrap', marginBottom: 0 }}>Descuento gral. (%)</label>
              <input
                style={{ ...st.input, ...sx.mono, width: 70, textAlign: 'right' }}
                type="text" inputMode="decimal"
                value={form.descuento_gral}
                onChange={e => setForm(f => ({ ...f, descuento_gral: limpiarNum(e.target.value) }))}
              />
            </div>

            {desglose ? (
              <>
                {/* Factura A: neto + IVA + percepciones + total (pedido Martin 2 sep) */}
                <div style={st.sepFiscal} />
                <div style={{ ...st.totalRow, fontSize: text.sec, color: colors.textSub }}>
                  <span>Neto gravado</span>
                  <span style={sx.mono}>${fmtMonto(desglose.neto)}</span>
                </div>
                <div style={{ ...st.totalRow, fontSize: text.sec, color: colors.textSub }}>
                  <span>IVA</span>
                  <span style={sx.mono}>${fmtMonto(desglose.iva)}</span>
                </div>
                {desglose.percep_iva > 0 && (
                  <div style={{ ...st.totalRow, fontSize: text.sec, color: colors.textSub }}>
                    <span>Percep. IVA</span>
                    <span style={sx.mono}>${fmtMonto(desglose.percep_iva)}</span>
                  </div>
                )}
                {desglose.percep_iibb > 0 && (
                  <div style={{ ...st.totalRow, fontSize: text.sec, color: colors.textSub }}>
                    <span>Percep. IIBB CABA{desglose.alicuota_iibb ? ` (${desglose.alicuota_iibb}%)` : ''}</span>
                    <span style={sx.mono}>${fmtMonto(desglose.percep_iibb)}</span>
                  </div>
                )}
                <div style={st.totalFinalRow}>
                  <span style={{ fontWeight: 700 }}>TOTAL (Factura A)</span>
                  <strong style={{ ...sx.mono, fontSize: 20, color: colors.link }}>${fmtMonto(totalFinal)}</strong>
                </div>
                <div style={st.notaEstimado}>
                  Estimado con la condición fiscal registrada del cliente — el importe final lo fija Presea al facturar.
                </div>
              </>
            ) : (
              <>
                <div style={st.totalFinalRow}>
                  <span style={{ fontWeight: 700 }}>TOTAL</span>
                  <strong style={{ ...sx.mono, fontSize: 20, color: colors.link }}>${fmtMonto(total)}</strong>
                </div>
                {percep?.aplica && (
                  <>
                    {percep.percep_iva > 0 && (
                      <div style={{ ...st.totalRow, fontSize: text.sec, color: colors.textSub }}>
                        <span>Percep. IVA</span>
                        <span style={sx.mono}>${fmtMonto(percep.percep_iva)}</span>
                      </div>
                    )}
                    {percep.percep_iibb > 0 && (
                      <div style={{ ...st.totalRow, fontSize: text.sec, color: colors.textSub }}>
                        <span>Percep. IIBB CABA{percep.alicuota_iibb ? ` (${percep.alicuota_iibb}%)` : ''}</span>
                        <span style={sx.mono}>${fmtMonto(percep.percep_iibb)}</span>
                      </div>
                    )}
                    <div style={st.totalRow}>
                      <span style={{ fontWeight: 600 }}>Total c/ percepciones</span>
                      <strong style={{ ...sx.mono, color: colors.link }}>${fmtMonto(totalFinal)}</strong>
                    </div>
                    <div style={st.notaEstimado}>
                      Estimado — el importe final lo fija Presea al facturar.
                    </div>
                  </>
                )}
              </>
            )}

            <div style={{ marginTop: space.lg }}>
              <Boton
                variante="primary"
                style={{ width: '100%' }}
                disabled={guardando || !form.cliente_id || form.items.length === 0}
                onClick={ejecutarGuardar}
              >
                {guardando ? 'Guardando...' : '💾 Crear nota de venta'}
              </Boton>
            </div>
            <div style={{ fontSize: text.meta, color: colors.textMeta, textAlign: 'center' }}>
              {form.items.length} {form.items.length === 1 ? 'renglón' : 'renglones'}
            </div>
          </div>
        </div>
      </div>

      {/* ── Modales (los mismos de la clásica) ── */}
      {articuloBuscandoIdx !== null && (
        <SelectorArticulosModal
          modo="simple"
          onSelect={handleArticuloSeleccionado}
          onClose={() => setArticuloBuscandoIdx(null)}
          precios={preciosLista}
          clienteId={form.cliente_id}
          lista={listaParaBuscar(listaEfectiva)}
        />
      )}
      {showPasteModal && (
        <PasteExcelModal
          clipboardText={pasteText}
          precios={preciosLista}
          onConfirm={handleConfirmPaste}
          onClose={() => { setShowPasteModal(false); setPasteText('') }}
        />
      )}
      {showCondFiscal && form.cliente && (
        <ModalCondicionFiscal
          cliente={form.cliente}
          onClose={() => setShowCondFiscal(false)}
          onSaved={(cl) => { setForm(f => ({ ...f, cliente: cl })); setShowCondFiscal(false) }}
        />
      )}
      {showEditarCliente && form.cliente && (
        <ModalAltaCliente
          cliente={form.cliente}
          onClose={() => setShowEditarCliente(false)}
          onGuardado={(cl) => { setForm(f => ({ ...f, cliente_id: cl.id, cliente: cl })); setShowEditarCliente(false) }}
        />
      )}
      {showAltaCliente && (
        <ModalAltaCliente onClose={() => setShowAltaCliente(false)} onCreado={handleClienteCreado} />
      )}
      {showTraerCarrito && (
        <TraerCarritoModal
          onClose={() => setShowTraerCarrito(false)}
          onTraer={handleTraerCarrito}
          sinCliente={!form.cliente_id}
          listaNv={userLista}
        />
      )}
      {showBuscarArticulos && (
        <SelectorArticulosModal
          onConfirm={handleArticulosMulti}
          onClose={() => setShowBuscarArticulos(false)}
          precios={preciosLista}
          clienteId={form.cliente_id}
          lista={listaParaBuscar(listaEfectiva)}
        />
      )}
      {/* Productos habituales — MISMO modal, acotado a la historia del cliente */}
      {showHabituales && (
        <SelectorArticulosModal
          onConfirm={handleArticulosMulti}
          onClose={() => setShowHabituales(false)}
          precios={preciosLista}
          clienteId={form.cliente_id}
          lista={listaParaBuscar(listaEfectiva)}
          habitualesDe={form.cliente_id}
          titulo={`Productos habituales de ${form.cliente?.nombre || 'este cliente'}`}
          avisos={['Lo que compró en los últimos 24 meses, lo más comprado primero. Si la lista sale vacía, todavía no le registramos compras.']}
        />
      )}
      {showBuscadorClienteModal && (
        <ModalListaClientes
          onSelect={(cl) => {
            setForm(f => ({ ...f, cliente_id: cl.id, cliente: cl }))
            setShowBuscadorClienteModal(false)
          }}
          onClose={() => setShowBuscadorClienteModal(false)}
        />
      )}
      {showVendedoresModal && (
        <ModalListaVendedores
          onSelect={(v) => setForm(f => ({ ...f, vendedor_codigo: String(v.codigo), vendedor_nombre: v.nombre }))}
          onClose={() => setShowVendedoresModal(false)}
        />
      )}
      {articuloPrecios && (
        <ModalPreciosArticulo
          articulo={articuloPrecios}
          onElegir={(precio) => { agregarItemConPrecio(articuloPrecios, precio); setArticuloPrecios(null) }}
          onClose={() => setArticuloPrecios(null)}
        />
      )}
    </div>
  )
}

const st = {
  wrap: { maxWidth: 1280 },
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: space.lg },
  title: { margin: 0, fontSize: text.title, fontWeight: 600, color: colors.textStrong },
  success: { background: colors.okBg, color: colors.ok, padding: `${space.md}px ${space.xl}px`, borderRadius: radius.sm, marginBottom: space.lg, fontWeight: 600, fontSize: text.body },
  errorBox: { background: colors.errBg, color: colors.err, padding: `${space.md}px ${space.xl}px`, borderRadius: radius.sm, marginBottom: space.lg, fontSize: text.body },

  grid: { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 300px', gap: space.lg, alignItems: 'start' },
  card: { ...sx.card, marginBottom: space.lg, display: 'flex', flexDirection: 'column', gap: space.md },

  articulosHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: space.md, flexWrap: 'wrap' },


  // minWidth: con tableLayout fixed, las columnas de ancho fijo se comen todo
  // en pantallas angostas y Descripción colapsaba a 0 — con el mínimo, la
  // descripción siempre tiene lugar y aparece scroll horizontal si no entra.
  tabla: { width: '100%', minWidth: 700, borderCollapse: 'collapse', fontSize: text.body, tableLayout: 'fixed' },
  // th del sistema pero con el MISMO padding horizontal que las celdas (8px):
  // con columnas de ancho fijo, el 16px de sx.th descuadraba header vs contenido.
  th: { ...sx.th, padding: `${space.md}px ${space.md}px` },
  // La celda de stock: una columna angosta por depósito, números a la derecha.
  // La misma grilla en la cabecera (con la referencia) y en cada renglón.
  td: { padding: `${space.xs}px ${space.md}px`, verticalAlign: 'middle', fontSize: text.body, color: colors.text },
  celdaInput: {
    width: '100%', boxSizing: 'border-box', padding: '5px 8px',
    border: `1px solid ${colors.textPlh}`, borderRadius: radius.xs,
    fontSize: text.body, textAlign: 'right', ...sx.mono,
  },
  descTexto: { minWidth: 0, lineHeight: 1.3 },
  badgeDetallePropio: { flexShrink: 0, fontSize: text.meta, color: colors.t80Text, background: colors.t80Bg, padding: '1px 5px', borderRadius: radius.xs, fontWeight: 600 },
  lupaBtn: { padding: '1px 5px', background: 'none', border: `1px solid ${colors.border}`, borderRadius: radius.xs, cursor: 'pointer', fontSize: text.meta, lineHeight: 1.4, color: colors.textSec, flexShrink: 0 },
  warnSinPrecio: { marginTop: 2, fontSize: text.meta, color: colors.warn, fontWeight: 600 },
  notaPrecioPropio: { marginTop: 2, fontSize: text.meta, color: colors.textSec, fontWeight: 500 },
  emptyItems: { color: colors.textMeta, textAlign: 'center', padding: `${space.xl}px 0`, fontSize: text.body, lineHeight: 1.6 },
  stockError: { fontSize: text.sec, color: colors.err, background: colors.errBg, border: `1px solid ${colors.errBorder}`, borderRadius: radius.sm, padding: '6px 10px' },

  sugerencia: { display: 'flex', gap: space.lg, alignItems: 'center', flexWrap: 'wrap', background: colors.linkBg, border: `1px solid ${colors.linkBorder}`, borderRadius: radius.md, padding: `${space.md}px ${space.lg}px`, fontSize: text.body, color: colors.text, lineHeight: 1.45 },
  cerrarSug: { background: 'none', border: 'none', color: colors.link, cursor: 'pointer', fontSize: 16, lineHeight: 1 },

  totalesCard: {
    ...sx.card, position: 'sticky', top: space.xl,
    display: 'flex', flexDirection: 'column', gap: space.md,
  },
  totalRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: text.body },
  totalFinalRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: text.body,
    borderTop: `2px solid ${colors.borderStrong}`, paddingTop: space.md, marginTop: space.xs,
  },
  sepFiscal: { borderTop: `1px solid ${colors.border}`, margin: `${space.xs}px 0` },
  notaEstimado: { fontSize: text.meta, color: colors.textMeta, lineHeight: 1.4 },

  label: { display: 'block', fontWeight: 600, fontSize: text.meta, color: colors.textSub, marginBottom: 3, whiteSpace: 'nowrap', lineHeight: 1 },
  // Sistema del boceto aprobado: TODOS los controles miden 28px de alto.
  input: { width: '100%', height: 28, padding: '0 9px', border: `1px solid ${colors.textPlh}`, borderRadius: radius.xs, fontSize: text.body, boxSizing: 'border-box', fontFamily: 'inherit' },
  row2: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: space.md },
  // Ficha del cliente condensada (boceto): 2 líneas sobre fondo celeste.
  cliInfo: { background: colors.infoSoft, border: `1px solid ${colors.infoBorder}`, borderRadius: radius.xs, padding: `5px 10px`, fontSize: text.sec, color: colors.info, lineHeight: 1.7, marginTop: space.md },
  f6Hint: { fontSize: text.sec, color: colors.textSec, marginTop: space.sm, display: 'block' },
  linkBtn: { border: 'none', background: 'none', padding: 0, cursor: 'pointer', color: colors.link, fontSize: text.meta, fontWeight: 500, textDecoration: 'underline' },
  // Datos generales (boceto): un renglón sobre columnas fijas + observaciones.
  gDG: { display: 'grid', gridTemplateColumns: 'minmax(190px, auto) minmax(150px, 1fr) 56px minmax(140px, 1fr) auto', gap: space.lg, alignItems: 'end', marginBottom: space.md },
  // Entrega (boceto): fecha · horario · detalles en un renglón.
  gENT: { display: 'grid', gridTemplateColumns: '150px 110px minmax(160px, 1fr)', gap: space.lg, alignItems: 'end', marginBottom: space.md },
  mktBox: { display: 'inline-flex', alignItems: 'center', gap: space.sm, fontSize: text.sec, cursor: 'pointer', background: colors.bgSoft, border: `1px solid ${colors.border}`, borderRadius: radius.xs, padding: '0 10px', height: 28, boxSizing: 'border-box', whiteSpace: 'nowrap' },
  mktBoxOn: { background: colors.t80Bg, border: `1px solid ${colors.t80Border}` },
  envioBox: { padding: space.lg, border: `1px solid ${colors.border}`, borderRadius: radius.md, background: colors.bgSoft },
  avisoInterior: { marginTop: space.sm, background: colors.warnBg, border: `1px solid ${colors.warnBorder}`, color: colors.warn, borderRadius: radius.md, padding: '8px 12px', fontSize: text.sec, fontWeight: 500 },
  cotBox: { marginTop: 8, background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: radius.md, padding: '10px 12px', fontSize: text.sec, color: colors.textSub },
}
